import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { devSubject } from './config';
import { demoPersonaOf } from './persona';

/** In a demo, the "voir comme…" choice travels with each request to that client. */
export function personaHeader(path: string): Record<string, string> {
  const tenant = /^\/api\/t\/([0-9a-f-]{36})\//.exec(path)?.[1];
  const persona = tenant ? demoPersonaOf(tenant) : null;
  return persona ? { 'x-demo-persona': persona } : {};
}
import type { Messages } from './messages';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: { error?: string; message?: string; issues?: unknown[]; reference?: string } | null,
  ) {
    super(body?.message ?? `HTTP ${status}`);
  }
}

/** Returns a fetch function that sends the user's access token and throws ApiError on failure. */
export function useApi() {
  const auth = useAuth();
  const token = auth.user?.access_token;

  return useCallback(
    async <T,>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> => {
      const res = await fetch(path, {
        method: init.method ?? 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(devSubject ? { 'x-dev-subject': devSubject } : {}),
          ...personaHeader(path),
          ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        body: init.body === undefined ? undefined : JSON.stringify(init.body),
      });
      const body = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
      if (!res.ok) throw new ApiError(res.status, body);
      return body as T;
    },
    [token],
  );
}

/** A user-facing message for a failed request. */
export function errorMessage(error: unknown, t: Messages): string {
  if (!(error instanceof ApiError)) return t.common.error;
  switch (error.status) {
    case 400:
      return t.common.invalid;
    case 403:
      return t.common.forbidden;
    case 404:
      return t.common.notFound;
    case 409:
      return t.common.conflict;
    case 422:
      return error.body?.message ?? t.common.invalid;
    default:
      return error.body?.reference ? t.common.errorWithReference(error.body.reference) : t.common.error;
  }
}

/** Authentication headers, for requests that are not JSON (file and audio uploads). */
export function useAuthHeaders(): () => Record<string, string> {
  const auth = useAuth();
  const token = auth.user?.access_token;
  return useCallback(() => ({ ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(devSubject ? { 'x-dev-subject': devSubject } : {}) }), [token]);
}
